import type { CSSProperties, ReactNode, RefObject } from 'react';
import { memo, useMemo, useRef, useState, isValidElement } from 'react';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeBareAutolink from '../lib/rehype-bare-autolink';
import { useTranslation } from '../lib/i18n';
import { useBasePath } from '../lib/use-base-path';
import { ScrollArea } from './symphony/ui';
import { stripBareDeeplinks } from '../lib/strip-bare-deeplinks';
import { safeUrl } from '../lib/safe-url';
import { markdownLinkTarget } from '../lib/markdown-link-target';
import { standaloneEntityLink } from '../lib/entity-link';
import { EntityChipRow } from './EntityChipRow';
import { chatLinkBiComponent } from '../lib/chat-link-bi';
import { useGenericBi } from '../lib/use-generic-bi';
import { useLeaveChatOnNavigate } from '../lib/use-leave-chat';
import { CHAT_HOME_BI } from './symphony/chat-surface/message-utils';

// Two variants:
//   - `compact` (default): the original pulse/task block style — small font,
//     subdued color, just paragraphs/headings/lists/code/links/strong.
//   - `chat`: full prose treatment for an assistant turn in the conversation.
//     Adds tables (with horizontal scroll on overflow), blockquotes (warm
//     brass left rule), thematic breaks, and code blocks, all sized to the
//     assistant bubble's 15px/1.62 reading column. GFM is enabled here so
//     pipe-tables and strikethrough render rather than dumping raw `|`/`~~`
//     into the message body.

type Variant = 'compact' | 'chat' | 'chat-inline';

/** The context the ONE non-pure renderer (`a`) needs. Held in a ref rather than a `useMemo` dep so the
 *  renderer's identity can be frozen: `leaveChat` and `router` are fresh objects on every render, so a
 *  dep array containing them rebuilds the map every time and the memoization silently does nothing —
 *  while still looking correct. The body still runs per render, so nothing here goes stale. */
type LinkCtx = {
  isChat: boolean;
  basePath: string;
  router: NextRouter;
  leaveChat: () => void;
  logPageClickBi: ReturnType<typeof useGenericBi>['logPageClickBi'];
};

export const Markdown = memo(function Markdown({
  children,
  variant = 'compact',
  dir,
  gfm,
}: {
  children: string;
  variant?: Variant;
  /** Whether to parse GitHub-flavoured markdown — tables, strikethrough, task lists, bare autolinks.
   *
   *  Defaults to on for the chat variants only, which is the historical behaviour: a pulse or task summary is
   *  a short prose line, so the extra grammar bought nothing there. **A rendered DOCUMENT must opt in.** A
   *  `.md` report is exactly where tables live, and without this its `| a | b |` rows render as literal pipes
   *  — which is what a markdown *preview* looked like before, i.e. the raw source it was meant to replace. */
  gfm?: boolean;
  /** Base writing direction for the whole block. Omitted keeps the historical behaviour (`auto` for the
   *  chat variants, unset for `compact`), which is right for a chat transcript — many short, independent
   *  messages, each entitled to its own base direction.
   *
   *  A SETTLED DOCUMENT is the opposite case and must pass an explicit value: `auto` is resolved by the
   *  browser per block element from its first strong character, so one report's ~15 paragraphs each vote
   *  separately and a mostly-Hebrew report lays out as a ragged mix of RTL prose and LTR `Status:` lines.
   *  See `lib/message-direction.ts` (`messageDir`) for the majority-script verdict to pass here. */
  dir?: 'auto' | 'rtl' | 'ltr';
}) {
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const leaveChat = useLeaveChatOnNavigate();
  const isChat = variant === 'chat' || variant === 'chat-inline';
  const containerStyle: CSSProperties = isChat
    ? { fontSize: 16, lineHeight: 1.62, color: 'rgba(var(--ink-rgb),0.88)' }
    : { fontSize: 14, lineHeight: 1.55, color: 'rgba(var(--ink-rgb),0.82)' };

  const ctx = useRef<LinkCtx>(null as unknown as LinkCtx);
  ctx.current = { isChat, basePath, router, leaveChat, logPageClickBi };

  // ⚠️ This map's IDENTITY is the whole point. `hast-util-to-jsx-runtime` looks each tag up here and
  // passes the result as the JSX element TYPE, so a renderer redefined per render changes `element.type`
  // and React unmounts + remounts the block. Inline arrows here meant every settled chat message was torn
  // down and rebuilt on every streamed chunk — measured at 1,248 paragraph re-insertions across 29 settled
  // rows during one reply, which is the chat's streaming flicker. Keep these frozen.
  const components = useMemo<Components>(() => ({ ...staticComponents(variant), a: makeAnchor(variant, ctx) }), [variant]);

  // Defensive: strip a bare `orion://…` deep-link the LLM occasionally hallucinates in
  // prose (no autolinker matches a non-http scheme, so it renders as inert text). A real
  // `](orion://…)` markdown target and any `https://…` link are left intact — see the helper.
  const source = stripBareDeeplinks(children);

  return (
    // Chat bubbles resolve their own base direction from content (dir="auto"), so a Hebrew/Arabic
    // message with embedded Latin tokens orders correctly even in an LTR-locale session (and vice-versa),
    // independent of the document-level `dir`. Not applied to the `compact` (pulse/task) variant.
    // An explicit `dir` prop overrides that guess — see the prop's own note.
    <div dir={dir ?? (isChat ? 'auto' : undefined)} style={containerStyle}>
      <ReactMarkdown
        remarkPlugins={gfm ?? isChat ? REMARK_CHAT : REMARK_NONE}
        // Linkify bare URLs in BOTH variants. A no-op when remark-gfm runs (it
        // already turned bare URLs into <a> before the hast stage, and the
        // plugin skips <a>/code/pre subtrees); the actual fix for a compact
        // block parsed without it.
        rehypePlugins={REHYPE}
        // The default transform's allowlist has no `orion:`, so it rewrote every in-app deep-link
        // target to `href=""` — a dead link that reloaded the page, and one the `a` renderer below
        // could no longer resolve. Ours keeps the same rejections and adds the app's own scheme.
        urlTransform={safeUrl}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
});

// ===== the frozen renderer maps =====
//
// Everything below is built ONCE (per variant) rather than per render. See the note on `components`
// above for why identity matters here.

const REMARK_CHAT = [remarkGfm];
const REMARK_NONE: typeof REMARK_CHAT = [];
const REHYPE = [rehypeBareAutolink];

const STATIC_CACHE = new Map<Variant, Components>();

/** Lazy on purpose: the style constants these renderers close over are declared below this point, so
 *  building the maps at module-init would hit their temporal dead zone. */
function staticComponents(variant: Variant): Components {
  let built = STATIC_CACHE.get(variant);
  if (!built) {
    built = buildStatic(variant);
    STATIC_CACHE.set(variant, built);
  }
  return built;
}

/** Every renderer that depends only on the VARIANT — i.e. all of them except `a`. Built per variant
 *  rather than as one shared map, because nearly all of them branch on `isChat`/`isInline`; a single
 *  module constant would silently give the `compact` (pulse/task/doc) surfaces chat styling. */
function buildStatic(variant: Variant): Components {
  const isChat = variant === 'chat' || variant === 'chat-inline';
  const isInline = variant === 'chat-inline';
  const h = isChat ? CHAT_H : COMPACT_H;
  // chat-inline = single paragraph reply; zero block margin so the text
  // sits flush like a plain string in the bubble.
  const p: CSSProperties = isInline ? { margin: 0 } : isChat ? CHAT_P : COMPACT_P;
  const ul = isChat ? CHAT_UL : COMPACT_UL;
  const li = isChat ? CHAT_LI : COMPACT_LI;
  const code = isChat ? CHAT_CODE : COMPACT_CODE;

  return {
    h1: ({ children: c }) => <h1 style={{ ...h, fontSize: isChat ? 24 : h.fontSize }}>{c}</h1>,
    h2: ({ children: c }) => <h2 style={{ ...h, fontSize: isChat ? 21 : h.fontSize }}>{c}</h2>,
    h3: ({ children: c }) => <h3 style={{ ...h, fontSize: isChat ? 18 : h.fontSize }}>{c}</h3>,
    h4: ({ children: c }) => <h4 style={h}>{c}</h4>,
    p: ({ children: c, node }) => {
      // A paragraph that is EXACTLY one in-app link renders as a tappable
      // entity card row (chat variant only); inline links stay ordinary.
      const entity = isChat ? standaloneEntityLink(node) : null;
      if (entity) return <EntityChipRow href={entity.href} label={entity.text} kind={entity.kind} />;
      return <p style={p}>{c}</p>;
    },
    ul: ({ children: c }) => <ul style={ul}>{c}</ul>,
    ol: ({ children: c }) => <ol style={ul}>{c}</ol>,
    li: ({ children: c }) => <li style={li}>{c}</li>,
    code: ({ children: c, className }) => {
      // react-markdown distinguishes inline code from fenced blocks via
      // the className the parent <pre> applies. Inline = no className;
      // block = `language-foo`. Render block code as a scrollable card
      // so wide pasted snippets don't blow out the reading column.
      const isBlock = !!className;
      if (isBlock) {
        return <code style={isChat ? CHAT_CODE_BLOCK : code}>{c}</code>;
      }
      return <code style={code}>{c}</code>;
    },
    pre: ({ children: c }) => {
      if (!isChat) return <pre style={{ margin: 0 }}>{c}</pre>;
      // Code-block card: header strip with language label + copy
      // button (the only structural element in the chat variant that
      // grows its own chrome; per-block cards beat one big
      // wrap-the-whole-message card because they don't depend on a
      // brittle "where does the document start" heuristic).
      const { code, language } = extractCodeFromPre(c);
      return (
        <CodeBlockCard language={language} rawCode={code}>
          {c}
        </CodeBlockCard>
      );
    },
    strong: ({ children: c }) => <strong style={{ fontWeight: 'var(--sym-weight-semibold)', color: 'rgba(var(--ink-rgb),0.96)' }}>{c}</strong>,
    em: ({ children: c }) => <em style={{ fontStyle: 'italic' }}>{c}</em>,
    del: ({ children: c }) => <del style={{ textDecoration: 'line-through', color: 'rgba(var(--ink-rgb),0.55)' }}>{c}</del>,
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        style={
          isChat
            ? {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 8,
                margin: '8px 0',
                border: '1px solid rgba(var(--hairline-rgb), 0.14)',
              }
            : { maxWidth: '100%', height: 'auto' }
        }
      />
    ),
    // GFM task lists render `- [ ]` / `- [x]` as an <input type=checkbox>
    // followed by the item text. Keep them non-interactive (we don't
    // round-trip state) but visually correct.
    input: ({ checked, type }) =>
      type === 'checkbox' ? <input type="checkbox" checked={!!checked} readOnly style={{ marginInlineEnd: 6, verticalAlign: 'middle' }} /> : null,
    // Chat-only enrichments — return defaults under `compact` so the
    // existing pulse/task surfaces are unchanged.
    blockquote: ({ children: c }) => (isChat ? <blockquote style={CHAT_BLOCKQUOTE}>{c}</blockquote> : <blockquote>{c}</blockquote>),
    hr: () => (isChat ? <hr style={CHAT_HR} /> : <hr />),
    table: ({ children: c }) =>
      isChat ? (
        <ScrollArea axis="horizontal" style={CHAT_TABLE_WRAP}>
          <table style={CHAT_TABLE}>{c}</table>
        </ScrollArea>
      ) : (
        <table>{c}</table>
      ),
    thead: ({ children: c }) => <thead style={isChat ? CHAT_THEAD : {}}>{c}</thead>,
    th: ({ children: c }) => <th style={isChat ? CHAT_TH : {}}>{c}</th>,
    td: ({ children: c }) => <td style={isChat ? CHAT_TD : {}}>{c}</td>,
  };
}

/** The one renderer that is not variant-pure — it navigates and reports BI. Its identity is still frozen
 *  (once per Markdown instance); the per-render context reaches it through the ref, so nothing goes
 *  stale even though the function is never rebuilt. */
function makeAnchor(variant: Variant, ctx: RefObject<LinkCtx>): Components['a'] {
  const a = variant === 'chat' || variant === 'chat-inline' ? CHAT_A : COMPACT_A;

  return function MarkdownAnchor({ children: c, href }) {
    const { isChat, basePath, router, leaveChat, logPageClickBi } = ctx.current;
    // A mobile universal-link the assistant surfaces (apps.nownia.com/time/... or
    // orion://time/...) dead-ends on the app-download page on desktop web — route it to the
    // equivalent in-app screen instead, navigating within the app rather than opening a tab.
    // A target whose scheme is not on the allowlist is not a link at all: the label renders
    // as ordinary text rather than an anchor that goes nowhere. See `lib/markdown-link-target`.
    const target = markdownLinkTarget(href);
    if (target.kind === 'plain') return <>{c}</>;
    // Report every link tapped inside a chat message. Before this, the two CTAs the assistant
    // writes most — "connect your calendar" and "premium plans" — fired NOTHING at all, so a
    // conversion funnel through them was invisible. One handler covers all of them: the
    // component is derived from the destination, and the raw url always rides
    // `additionalFields` so an unclassified target is still attributed. Chat variants only —
    // the `compact` (pulse/task) variant is not a conversation.
    const reportLinkClick = (destination: string | undefined) => {
      if (!isChat) return;
      logPageClickBi(
        { ...CHAT_HOME_BI, button_name: 'chat_link' },
        {
          componentName: chatLinkBiComponent(destination),
          additionalFields: { url: destination },
        },
      );
    };
    if (target.kind === 'in-app') {
      const as = `${basePath}${target.route}`;
      return (
        <a
          href={as}
          style={a}
          onClick={(e) => {
            // Report before the modifier-key bail-out: a cmd-click still opens the link,
            // it just does so in a new tab instead of navigating this one.
            reportLinkClick(target.route);
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            // The chat is an overlay: on a phone every open size covers the whole
            // viewport, so navigating without collapsing it lands the user on a page
            // they can't see. Same adjustment every other in-app nav link makes.
            leaveChat();
            void router.push(target.route, as);
          }}
        >
          {c}
        </a>
      );
    }
    return (
      <a href={target.href} style={a} target="_blank" rel="noreferrer" onClick={() => reportLinkClick(target.href)}>
        {c}
      </a>
    );
  };
}

// ===== compact (legacy pulse/task) styles =====

const COMPACT_H: CSSProperties = {
  margin: '14px 0 6px',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgba(var(--ink-rgb),0.96)',
};

const COMPACT_P: CSSProperties = { margin: '0 0 8px' };
const COMPACT_UL: CSSProperties = { margin: '0 0 8px', paddingInlineStart: 20 };
const COMPACT_LI: CSSProperties = { margin: '2px 0' };

const COMPACT_CODE: CSSProperties = {
  padding: '2px 5px',
  borderRadius: 4,
  background: 'rgba(var(--hairline-rgb),0.06)',
  fontSize: 13,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const COMPACT_A: CSSProperties = {
  // Ink text + accent underline — the lime accent alone is too low-contrast on light surfaces (a11y).
  color: 'rgba(var(--ink-rgb), 0.9)',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(var(--accent-rgb), 0.85)',
};

// ===== chat (assistant message) styles =====

const CHAT_H: CSSProperties = {
  margin: '20px 0 8px',
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgba(var(--ink-rgb),0.96)',
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
};

const CHAT_P: CSSProperties = { margin: '0 0 12px' };
const CHAT_UL: CSSProperties = { margin: '0 0 12px', paddingInlineStart: 22 };
const CHAT_LI: CSSProperties = { margin: '4px 0' };

const CHAT_CODE: CSSProperties = {
  padding: '2px 6px',
  borderRadius: 4,
  background: 'rgba(var(--hairline-rgb),0.08)',
  fontSize: 14.5,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const CHAT_CODE_BLOCK: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 14,
  display: 'block',
};

const CHAT_A: CSSProperties = {
  // Ink-colored text (readable on the light message surface — the lime accent alone fails contrast) with an
  // accent-colored underline so it still reads as a link. Works in both themes since --ink adapts.
  color: 'rgba(var(--ink-rgb), 0.92)',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(var(--accent-rgb), 0.85)',
  textUnderlineOffset: 2,
};

const CHAT_BLOCKQUOTE: CSSProperties = {
  margin: '0 0 14px',
  padding: '4px 14px',
  borderInlineStart: '3px solid rgba(var(--accent-rgb), 0.45)',
  color: 'rgba(var(--ink-rgb), 0.72)',
  fontStyle: 'italic',
};

const CHAT_HR: CSSProperties = {
  margin: '20px 0',
  border: 'none',
  borderTop: '1px solid rgba(var(--hairline-rgb), 0.18)',
};

// Tables can be wider than the 720px reading column on Symphony's cream
// surface; wrap with horizontal scroll instead of clipping.
const CHAT_TABLE_WRAP: CSSProperties = {
  margin: '0 0 14px',
  borderRadius: 8,
  border: '1px solid rgba(var(--hairline-rgb), 0.14)',
};

const CHAT_TABLE: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const CHAT_THEAD: CSSProperties = {
  background: 'rgba(var(--hairline-rgb), 0.06)',
};

const CHAT_TH: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'start',
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgba(var(--ink-rgb), 0.9)',
  borderBottom: '1px solid rgba(var(--hairline-rgb), 0.16)',
};

const CHAT_TD: CSSProperties = {
  padding: '9px 12px',
  borderTop: '1px solid rgba(var(--hairline-rgb), 0.08)',
  color: 'rgba(var(--ink-rgb), 0.85)',
  verticalAlign: 'top',
};

// ===== code block card =====
//
// react-markdown nests fenced code as `<pre><code class="language-foo">text
// </code></pre>`. We render a wrapping <div> with a header strip (language
// label + Copy button) above the existing <pre> so the structural block
// announces itself without needing whole-message detection. Raw code text
// is extracted from the child <code>'s string children for clipboard use.

// Fence language tag → file extension for the Download button. Unknown
// languages fall back to .txt — a wrong-but-openable file beats a download
// the OS can't associate.
const LANGUAGE_EXTENSIONS: Record<string, string> = {
  javascript: 'js',
  js: 'js',
  jsx: 'jsx',
  typescript: 'ts',
  ts: 'ts',
  tsx: 'tsx',
  python: 'py',
  py: 'py',
  html: 'html',
  css: 'css',
  json: 'json',
  bash: 'sh',
  sh: 'sh',
  shell: 'sh',
  zsh: 'sh',
  sql: 'sql',
  yaml: 'yml',
  yml: 'yml',
  markdown: 'md',
  md: 'md',
  java: 'java',
  kotlin: 'kt',
  swift: 'swift',
  scala: 'scala',
  go: 'go',
  rust: 'rs',
  ruby: 'rb',
  rb: 'rb',
  c: 'c',
  cpp: 'cpp',
  csharp: 'cs',
  cs: 'cs',
  php: 'php',
  xml: 'xml',
  svg: 'svg',
};

function CodeBlockCard({ language, rawCode, children }: { language: string; rawCode: string; children: ReactNode }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard gated by focus or permissions; fail silently.
    }
  };
  const onDownload = () => {
    const extension = LANGUAGE_EXTENSIONS[language.toLowerCase()] ?? 'txt';
    const blob = new Blob([rawCode], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `snippet.${extension}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <div
      style={{
        margin: '12px 0',
        background: 'rgba(var(--hairline-rgb), 0.08)',
        border: '1px solid rgba(var(--hairline-rgb), 0.14)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          background: 'rgba(var(--hairline-rgb), 0.05)',
          borderBottom: '1px solid rgba(var(--hairline-rgb), 0.1)',
          fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'rgba(var(--ink-rgb), 0.5)',
        }}
      >
        <span>{language || t('markdown.codeFallback')}</span>
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <button
            type="button"
            onClick={onDownload}
            aria-label={t('markdown.downloadFile')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              background: 'rgba(var(--surface-base-rgb), 0.5)',
              border: '1px solid rgba(var(--hairline-rgb), 0.16)',
              borderRadius: 5,
              color: 'rgba(var(--ink-rgb), 0.7)',
              fontSize: 10.5,
              letterSpacing: '0.04em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden>
              <path
                d="M7 2v7M7 9l-3-3M7 9l3-3M2.5 11.5h9"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            {t('markdown.download')}
          </button>
          <button
            type="button"
            onClick={onCopy}
            aria-label={copied ? t('markdown.codeCopied') : t('markdown.copyCode')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              background: 'rgba(var(--surface-base-rgb), 0.5)',
              border: '1px solid rgba(var(--hairline-rgb), 0.16)',
              borderRadius: 5,
              color: copied ? 'rgba(var(--accent-rgb), 1)' : 'rgba(var(--ink-rgb), 0.7)',
              fontSize: 10.5,
              letterSpacing: '0.04em',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'color 140ms ease, background-color 140ms ease',
            }}
          >
            {copied ? (
              <>
                <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden>
                  <path
                    d="M2.5 6.5l2.3 2.3L9.5 3.6"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                {t('markdown.copied')}
              </>
            ) : (
              <>
                <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden>
                  <rect x="3.5" y="3.5" width="7" height="8.5" rx="1.3" stroke="currentColor" strokeWidth="1.1" fill="none" />
                  <path
                    d="M5.5 3.5V2.6c0-.55.45-1 1-1H10c.55 0 1 .45 1 1V8c0 .55-.45 1-1 1H9.5"
                    stroke="currentColor"
                    strokeWidth="1.1"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t('markdown.copy')}
              </>
            )}
          </button>
        </span>
      </div>
      <ScrollArea
        component="pre"
        axis="both"
        style={{
          margin: 0,
          padding: '12px 14px',
          fontSize: 13,
          lineHeight: 1.5,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
      >
        {children}
      </ScrollArea>
    </div>
  );
}

// Pull raw code text + language from the children react-markdown hands us.
// `<pre>` wraps a single `<code className="language-xxx">…</code>` element
// whose children prop is the literal text. Returns empty strings if the
// shape doesn't match (defensive — react-markdown can pass other nodes in
// unusual edge cases).
function extractCodeFromPre(node: ReactNode): { code: string; language: string } {
  const arr = Array.isArray(node) ? node : [node];
  for (const child of arr) {
    if (!isValidElement(child)) continue;
    const props = child.props as { className?: string; children?: ReactNode };
    const className = props.className ?? '';
    const langMatch = /language-(\S+)/.exec(className);
    const language = langMatch ? langMatch[1] : '';
    const inner = props.children;
    let code = '';
    if (typeof inner === 'string') {
      code = inner;
    } else if (Array.isArray(inner)) {
      code = inner.map((c) => (typeof c === 'string' ? c : '')).join('');
    }
    return { code, language };
  }
  return { code: '', language: '' };
}
